<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useRoute } from "vue-router";

import {
  attachBriefingRecording,
  fetchStaffBriefingPdf,
  getDownloadUrl,
  getProject,
  getSiteDiary,
  getStaffBriefing,
  getStaffBriefingHistory,
  recheckBriefingCoverage,
  reviewBriefing,
  uploadFile,
  type BriefingReviewFinding,
} from "@/api/client";
import BriefingContentEditor from "@/components/BriefingContentEditor.vue";
import PageLayout from "@/components/PageLayout.vue";

const route = useRoute();
const id = computed(() => route.params.id as string);

const { data, isPending, isError, error, refetch } = useQuery({
  queryKey: ["staff-briefing", id],
  queryFn: () => getStaffBriefing(id.value),
  // While the worker is transcribing, poll so the transcript + coverage appear.
  refetchInterval: (query) =>
    query.state.data?.transcription_status === "pending" ? 8000 : false,
});

// The project's site hazards — pre-ticked / flagged in the content editor.
const projectId = computed(() => data.value?.project_id ?? null);
const { data: project } = useQuery({
  queryKey: ["project", projectId],
  queryFn: () => getProject(projectId.value as string),
  enabled: () => !!projectId.value,
});

// ---- Briefing recording (record in-browser or upload) + AI coverage ----
const recState = ref<"idle" | "recording" | "busy">("idle");
const recError = ref<string | null>(null);
let mediaRecorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let startedAt = 0;

async function startRecording(): Promise<void> {
  recError.value = null;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    chunks = [];
    mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mediaRecorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: mediaRecorder?.mimeType || "audio/webm" });
      const seconds = (Date.now() - startedAt) / 1000;
      void uploadAndAttach(blob, seconds);
    };
    startedAt = Date.now();
    mediaRecorder.start();
    recState.value = "recording";
  } catch (e) {
    recError.value = e instanceof Error ? e.message : "Microphone unavailable";
  }
}

function stopRecording(): void {
  if (mediaRecorder && recState.value === "recording") {
    recState.value = "busy";
    mediaRecorder.stop();
  }
}

async function onUploadAudio(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (file) await uploadAndAttach(file, null);
  (e.target as HTMLInputElement).value = "";
}

async function uploadAndAttach(
  blob: Blob,
  durationSeconds: number | null,
): Promise<void> {
  recState.value = "busy";
  recError.value = null;
  try {
    const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
    const file = new File([blob], `briefing-${id.value}.${ext}`, { type: blob.type });
    const fileId = await uploadFile(file, "briefing_recording");
    await attachBriefingRecording(id.value, {
      file_id: fileId,
      duration_seconds: durationSeconds ?? undefined,
    });
    await refetch();
  } catch (e) {
    recError.value = e instanceof Error ? e.message : String(e);
  } finally {
    recState.value = "idle";
  }
}

const audioUrl = ref<string | null>(null);
watch(
  () => data.value?.recording_file_id,
  async (fileId) => {
    audioUrl.value = fileId ? await getDownloadUrl(fileId) : null;
  },
  { immediate: true },
);

const rechecking = ref(false);
async function recheck(): Promise<void> {
  rechecking.value = true;
  try {
    await recheckBriefingCoverage(id.value);
    await refetch();
  } finally {
    rechecking.value = false;
  }
}

const missedTopics = computed(
  () => (data.value?.coverage ?? []).filter((t) => !t.covered),
);

// --- Pre-delivery consistency review (Phase 3) ---
const reviewBusy = ref(false);
const reviewMsg = ref<string | null>(null);
const reviewFindings = ref<BriefingReviewFinding[] | null>(null);
async function runReview(): Promise<void> {
  reviewBusy.value = true;
  reviewMsg.value = null;
  try {
    const r = await reviewBriefing(id.value);
    reviewFindings.value = r.findings;
    reviewMsg.value = r.ok
      ? "Register looks complete — nothing flagged."
      : `${r.findings.length} thing(s) to check before close.`;
  } catch (e) {
    reviewMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    reviewBusy.value = false;
  }
}

function fmtDuration(seconds: number | null): string {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

const { data: history, refetch: refetchHistory } = useQuery({
  queryKey: ["staff-briefing-history", id],
  queryFn: () => getStaffBriefingHistory(id.value),
});

// If this briefing is for a shift (diary-linked), the diary's allocated staff
// are who's *expected* — compare against who's actually signed on.
const diaryId = computed(() => data.value?.diary_id ?? null);
const { data: diary } = useQuery({
  queryKey: ["site-diary", diaryId],
  queryFn: () => getSiteDiary(diaryId.value as string),
  enabled: () => !!diaryId.value,
});

const attendance = computed(() => {
  const expected = (diary.value?.staff ?? [])
    .map((s) => String((s as Record<string, unknown>).name ?? "").trim())
    .filter(Boolean);
  if (!expected.length) return null;
  const signed = new Set(
    (data.value?.signons ?? []).map((s) =>
      String((s as Record<string, unknown>).name ?? "").trim().toLowerCase(),
    ),
  );
  const missing = expected.filter((n) => !signed.has(n.toLowerCase()));
  return { expected: expected.length, signed: expected.length - missing.length, missing };
});

async function onContentSaved(): Promise<void> {
  await Promise.all([refetch(), refetchHistory()]);
}

function attribution(
  name: string | null | undefined,
  at: string | null | undefined,
): string | null {
  if (!name && !at) return null;
  return [name || "—", at ? fmtDateTime(at) : null].filter(Boolean).join(" · ");
}

async function openPdf(): Promise<void> {
  const blob = await fetchStaffBriefingPdf(id.value);
  window.open(URL.createObjectURL(blob), "_blank");
}

const cell = (row: Record<string, unknown>, key: string): string => {
  const value = row[key];
  return value === undefined || value === null ? "—" : String(value);
};

// The drawn signature, a data:image/png URI, if present.
const signature = (s: Record<string, unknown>): string | null =>
  typeof s.signature_data === "string" && s.signature_data ? s.signature_data : null;

const fmtDateTime = (iso: unknown): string =>
  typeof iso === "string" && iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : "—";

// Geo-stamp {lat,lng,accuracy_m} → coords + a maps link.
function geo(s: Record<string, unknown>): { label: string; href: string } | null {
  const g = s.gps as Record<string, unknown> | null | undefined;
  if (!g || typeof g !== "object") return null;
  const lat = Number(g.lat);
  const lng = Number(g.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  const acc = g.accuracy_m != null ? ` ±${Math.round(Number(g.accuracy_m))}m` : "";
  return {
    label: `${lat.toFixed(5)}, ${lng.toFixed(5)}${acc}`,
    href: `https://www.google.com/maps?q=${lat},${lng}`,
  };
}
</script>

<template>
  <PageLayout>
    <RouterLink
      :to="{ name: 'briefings', params: { projectId: $route.params.projectId } }"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Briefings
    </RouterLink>

    <div
      v-if="isPending"
      class="text-neutral-500 mt-4"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4 mt-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid gap-6"
    >
      <section class="rounded-xl bg-neutral-0 shadow p-6">
        <div class="flex items-start justify-between gap-4">
          <h1 class="text-xl font-semibold">
            {{ data.project_name }}
          </h1>
          <button
            type="button"
            class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            @click="openPdf"
          >
            Open PDF
          </button>
        </div>
        <dl
          class="mt-4 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm"
        >
          <dt class="text-neutral-500">
            Date
          </dt>
          <dd>{{ data.briefing_date }}</dd>
          <dt class="text-neutral-500">
            Task
          </dt>
          <dd>{{ data.task_description ?? "—" }}</dd>
          <dt class="text-neutral-500">
            Principal contractor
          </dt>
          <dd>{{ data.principal_contractor ?? "—" }}</dd>
          <dt class="text-neutral-500">
            Briefed by
          </dt>
          <dd>{{ data.briefer.name ?? "—" }}</dd>
          <dt class="text-neutral-500">
            Delivered
          </dt>
          <dd>
            {{ data.delivered ? "Yes" : "No" }}
            <span
              v-if="data.delivered && data.delivered_at"
              class="text-neutral-400"
            > · {{ fmtDateTime(data.delivered_at) }}</span>
          </dd>
          <template v-if="data.delivered_signature">
            <dt class="text-neutral-500">
              Briefer signature
            </dt>
            <dd>
              <a
                :href="data.delivered_signature"
                target="_blank"
                rel="noopener"
                title="Open full size"
              >
                <img
                  :src="data.delivered_signature"
                  alt="briefer signature"
                  class="h-10 w-28 rounded border border-neutral-200 bg-white object-contain"
                >
              </a>
            </dd>
          </template>
          <template v-if="attribution(data.created_by_name, data.created_at)">
            <dt class="text-neutral-500">
              Created by
            </dt>
            <dd>{{ attribution(data.created_by_name, data.created_at) }}</dd>
          </template>
          <template v-if="attribution(data.delivered_by_name, data.delivered_at)">
            <dt class="text-neutral-500">
              Delivered by
            </dt>
            <dd>{{ attribution(data.delivered_by_name, data.delivered_at) }}</dd>
          </template>
          <template v-if="attribution(data.closed_by_name, data.closed_at)">
            <dt class="text-neutral-500">
              Closed by
            </dt>
            <dd>{{ attribution(data.closed_by_name, data.closed_at) }}</dd>
          </template>
          <template
            v-if="attribution(data.last_updated_by_name, data.last_updated_at)"
          >
            <dt class="text-neutral-500">
              Last edited by
            </dt>
            <dd>{{ attribution(data.last_updated_by_name, data.last_updated_at) }}</dd>
          </template>
        </dl>
      </section>

      <!-- What was briefed — read + edit (BriefingContentUpdated) -->
      <BriefingContentEditor
        :briefing-id="id"
        :content="data.briefed_content"
        :hazards="project?.hazards ?? []"
        @saved="onContentSaved"
      />

      <!-- Pre-delivery consistency review (advisory) -->
      <section class="rounded-xl bg-neutral-0 shadow p-6">
        <div class="flex items-start justify-between gap-4">
          <h2 class="font-semibold">
            Pre-delivery review
          </h2>
          <button
            type="button"
            class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="reviewBusy"
            @click="runReview"
          >
            {{ reviewBusy ? "Checking…" : "Run review" }}
          </button>
        </div>
        <p
          v-if="reviewMsg"
          class="mt-3 text-sm"
          :class="reviewFindings && reviewFindings.length
            ? 'text-neutral-700'
            : 'text-semantic-success'"
        >
          {{ reviewMsg }}
        </p>
        <ul
          v-if="reviewFindings && reviewFindings.length"
          class="mt-3 grid gap-1.5"
        >
          <li
            v-for="(f, i) in reviewFindings"
            :key="i"
            class="flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
            :class="f.severity === 'warning'
              ? 'border-semantic-warning/40 bg-semantic-warning/5'
              : 'border-neutral-200 bg-neutral-50'"
          >
            <span class="shrink-0">{{ f.severity === "warning" ? "⚠️" : "💡" }}</span>
            <span>{{ f.message }}</span>
          </li>
        </ul>
        <p
          v-else-if="reviewFindings === null"
          class="mt-3 text-sm text-neutral-400"
        >
          Check sign-on signatures, delivery, work-pack link and topic coverage
          before closing the register.
        </p>
      </section>

      <!-- Briefing recording + AI coverage check -->
      <section class="rounded-xl bg-neutral-0 shadow p-6">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="font-semibold">
              Briefing recording
            </h2>
            <p class="mt-0.5 text-sm text-neutral-500">
              Record the briefing as it's delivered. Kept for 90 days, then
              automatically deleted. AI checks it covered the required topics.
            </p>
          </div>
          <span
            v-if="data.recording_recorded_at && !data.recording_purged"
            class="shrink-0 text-xs text-neutral-400"
          >Auto-deletes {{ data.recording_retention_until
            ? new Date(data.recording_retention_until).toLocaleDateString("en-GB")
            : "" }}</span>
        </div>

        <!-- Capture prompt (no recording yet) -->
        <div
          v-if="!data.recording_file_id && !data.recording_purged"
          class="mt-4 flex flex-wrap items-center gap-2"
        >
          <button
            v-if="recState !== 'recording'"
            type="button"
            class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-brand-primary-on hover:opacity-90 disabled:opacity-50"
            :disabled="recState === 'busy'"
            @click="startRecording"
          >
            {{ recState === "busy" ? "Uploading…" : "● Record briefing" }}
          </button>
          <button
            v-else
            type="button"
            class="rounded-md bg-semantic-danger px-3 py-1.5 text-sm font-medium text-neutral-0 hover:opacity-90"
            @click="stopRecording"
          >
            ■ Stop &amp; save
          </button>
          <span class="text-xs text-neutral-400">or</span>
          <label class="cursor-pointer rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50">
            Upload audio
            <input
              type="file"
              accept="audio/*"
              class="hidden"
              :disabled="recState !== 'idle'"
              @change="onUploadAudio"
            >
          </label>
          <span
            v-if="recState === 'recording'"
            class="text-sm text-semantic-danger"
          >Recording…</span>
        </div>
        <p
          v-if="recError"
          class="mt-2 text-sm text-semantic-danger"
        >
          {{ recError }}
        </p>

        <!-- Recording present -->
        <div
          v-if="data.recording_file_id && audioUrl"
          class="mt-4"
        >
          <audio
            :src="audioUrl"
            controls
            class="w-full"
          />
          <p
            v-if="data.recording_duration_seconds"
            class="mt-1 text-xs text-neutral-400"
          >
            Duration {{ fmtDuration(data.recording_duration_seconds) }}
          </p>
        </div>
        <p
          v-else-if="data.recording_purged"
          class="mt-4 text-sm text-neutral-500"
        >
          The audio has passed its 90-day retention and was deleted. The
          transcript and coverage check below are kept as the record.
        </p>

        <!-- Transcription status -->
        <p
          v-if="data.transcription_status === 'pending'"
          class="mt-3 text-sm text-semantic-warning"
        >
          Transcribing… the coverage check will appear automatically.
        </p>
        <p
          v-else-if="data.transcription_status === 'failed'"
          class="mt-3 text-sm text-semantic-danger"
        >
          Transcription failed{{ data.transcription_error ? `: ${data.transcription_error}` : "" }}.
        </p>

        <!-- AI coverage result -->
        <div
          v-if="data.coverage && data.coverage.length"
          class="mt-4 rounded-lg border border-neutral-200 p-4"
        >
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="text-sm font-semibold text-neutral-900">
              AI coverage check
            </h3>
            <span
              v-if="!missedTopics.length"
              class="rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs text-semantic-success"
            >All topics covered</span>
            <span
              v-else
              class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs font-medium text-semantic-warning"
            >{{ missedTopics.length }} not covered</span>
            <button
              type="button"
              class="ml-auto text-xs text-neutral-400 hover:text-neutral-900"
              :disabled="rechecking"
              @click="recheck"
            >
              {{ rechecking ? "Re-checking…" : "Re-check" }}
            </button>
          </div>
          <ul class="mt-2 grid gap-1">
            <li
              v-for="t in data.coverage"
              :key="t.topic"
              class="flex items-start gap-2 text-sm"
            >
              <span
                :class="t.covered ? 'text-semantic-success' : 'text-semantic-danger'"
                class="mt-0.5"
              >{{ t.covered ? "✓" : "✗" }}</span>
              <span>
                <span :class="t.covered ? 'text-neutral-700' : 'text-neutral-900 font-medium'">{{ t.topic }}</span>
                <span
                  v-if="t.evidence"
                  class="text-neutral-400"
                > — “{{ t.evidence }}”</span>
              </span>
            </li>
          </ul>
          <p
            v-if="data.coverage_summary"
            class="mt-2 text-sm text-neutral-500"
          >
            {{ data.coverage_summary }}
          </p>
        </div>

        <!-- Transcript (collapsible) -->
        <details
          v-if="data.transcript"
          class="mt-3"
        >
          <summary class="cursor-pointer text-sm text-neutral-500 hover:text-neutral-900">
            Transcript
          </summary>
          <p class="mt-2 whitespace-pre-wrap rounded-lg bg-neutral-50 p-3 text-sm text-neutral-700">
            {{ data.transcript }}
          </p>
        </details>
      </section>

      <section class="rounded-xl bg-neutral-0 shadow overflow-hidden">
        <h2 class="font-semibold px-6 pt-6 pb-1">
          Sign-on register ({{ data.signons.length }})
        </h2>
        <p
          v-if="attendance"
          class="px-6 pb-3 text-sm"
          :class="attendance.missing.length ? 'text-semantic-warning' : 'text-neutral-500'"
        >
          {{ attendance.signed }} of {{ attendance.expected }} allocated signed on.
          <span v-if="attendance.missing.length">
            Not yet signed on: {{ attendance.missing.join(", ") }}.
          </span>
        </p>
        <div
          v-if="data.signons.length"
          class="overflow-x-auto"
        >
          <table class="w-full text-sm">
            <thead class="bg-neutral-50 text-neutral-500 text-left">
              <tr>
                <th class="px-4 py-2 font-medium">
                  Name
                </th>
                <th class="px-4 py-2 font-medium">
                  Site duty
                </th>
                <th class="px-4 py-2 font-medium">
                  Signature
                </th>
                <th class="px-4 py-2 font-medium">
                  Briefed
                </th>
                <th class="px-4 py-2 font-medium">
                  Signed at
                </th>
                <th class="px-4 py-2 font-medium">
                  Location
                </th>
                <th class="px-4 py-2 font-medium">
                  Sentinel
                </th>
                <th class="px-4 py-2 font-medium">
                  In
                </th>
                <th class="px-4 py-2 font-medium">
                  Out
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(s, i) in data.signons"
                :key="i"
                class="border-t border-neutral-100"
              >
                <td class="px-4 py-2 whitespace-nowrap">
                  {{ cell(s, "name") }}
                  <span
                    v-if="s.is_visitor"
                    class="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700"
                  >Visitor</span>
                  <span class="block text-xs text-neutral-400">{{ cell(s, "company") }}</span>
                </td>
                <td class="px-4 py-2">
                  {{ s.is_visitor ? "Visitor" : cell(s, "site_duty") }}
                </td>
                <td class="px-4 py-2">
                  <a
                    v-if="signature(s)"
                    :href="signature(s)!"
                    target="_blank"
                    rel="noopener"
                    title="Open full size"
                  >
                    <img
                      :src="signature(s)!"
                      alt="signature"
                      class="h-10 w-28 rounded border border-neutral-200 bg-white object-contain"
                    >
                  </a>
                  <span
                    v-else
                    class="text-semantic-warning"
                  >Not signed</span>
                </td>
                <td class="px-4 py-2">
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="s.briefing_acknowledged
                      ? 'bg-semantic-success/10 text-semantic-success'
                      : 'bg-neutral-100 text-neutral-500'"
                  >{{ s.briefing_acknowledged ? "Yes" : "No" }}</span>
                </td>
                <td class="px-4 py-2 whitespace-nowrap text-neutral-600">
                  {{ fmtDateTime(s.signed_on_at) }}
                </td>
                <td class="px-4 py-2 whitespace-nowrap">
                  <a
                    v-if="geo(s)"
                    :href="geo(s)!.href"
                    target="_blank"
                    rel="noopener"
                    class="text-brand-primary hover:underline"
                  >📍 {{ geo(s)!.label }}</a>
                  <span
                    v-else
                    class="text-neutral-400"
                  >—</span>
                </td>
                <td class="px-4 py-2">
                  {{ cell(s, "sentinel_number") }}
                </td>
                <td class="px-4 py-2">
                  {{ cell(s, "time_in") }}
                </td>
                <td class="px-4 py-2">
                  {{ cell(s, "time_out") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p
          v-else
          class="text-sm text-neutral-500 px-6 pb-6"
        >
          No one has signed on yet.
        </p>
      </section>

      <!-- Audit trail -->
      <section class="rounded-xl bg-neutral-0 shadow p-6">
        <h2 class="mb-3 font-semibold">
          History
        </h2>
        <ol
          v-if="history && history.length"
          class="grid gap-2 text-sm"
        >
          <li
            v-for="h in history"
            :key="h.seq"
            class="flex flex-wrap items-baseline gap-x-2"
          >
            <span class="font-medium text-neutral-900">{{ h.label }}</span>
            <span class="text-neutral-500">{{ h.actor_name ?? "—" }}</span>
            <span class="text-neutral-400">· {{ fmtDateTime(h.at) }}</span>
          </li>
        </ol>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          No history yet.
        </p>
      </section>
    </div>
  </PageLayout>
</template>
